import React, { useEffect, useState } from 'react';
import { Percent, X } from 'lucide-react';
import { getThemeColor } from '../../config/appConfig';
import apiService from '../../utils/apiService';

const THEME = getThemeColor('primary');

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== '') return obj[key];
  }
  return undefined;
};

const formatPct = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n % 1 === 0 ? n.toFixed(0) : String(n);
};

const normalizeCommissions = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.result ?? payload?.data ?? payload?.Result ?? [];
  if (!Array.isArray(list)) return [];

  return list
    .map((row) => {
      const id = pick(row, 'affiliateSubCommissionId', 'AffiliateSubCommissionId', 'id');
      const percentage = Number(pick(row, 'percentage', 'Percentage'));
      const isActive = pick(row, 'isActive', 'IsActive');
      if (!Number.isFinite(percentage)) return null;
      if (isActive === false || isActive === 0 || String(isActive).toLowerCase() === 'false') {
        return null;
      }
      return {
        id: id != null ? String(id) : String(percentage),
        percentage,
        label: `${formatPct(percentage)}%`,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.percentage - b.percentage);
};

const UpdateCommissionModal = ({ open, referral, onClose, onSave, saving = false }) => {
  const [percentage, setPercentage] = useState('');
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;

    const current = referral?.percentage;
    setPercentage(current != null && current !== '' ? String(current) : '');
    setError('');
    setOptions([]);

    let cancelled = false;
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const res = await apiService.getAffiliateSubCommissions();
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || res.message || 'Failed to load commission options');
          return;
        }
        let next = normalizeCommissions(res.data ?? res.result ?? res);
        if (
          current != null &&
          current !== '' &&
          Number.isFinite(Number(current)) &&
          !next.some((opt) => Number(opt.percentage) === Number(current))
        ) {
          next = [
            ...next,
            {
              id: `current-${current}`,
              percentage: Number(current),
              label: `${formatPct(current)}%`,
            },
          ].sort((a, b) => a.percentage - b.percentage);
        }
        setOptions(next);
        if ((current == null || current === '') && next.length > 0) {
          setPercentage(String(next[0].percentage));
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load commission options');
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    };

    loadOptions();

    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelled = true;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, saving, referral]);

  if (!open || !referral) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (percentage === '' || percentage == null) {
      setError('Please select a commission percentage');
      return;
    }
    const ok = await onSave?.(referral, Number(percentage));
    if (ok) onClose?.();
  };

  return (
    <div className="raf-modal" role="presentation">
      <button
        type="button"
        className="raf-modal__backdrop"
        onClick={() => !saving && onClose?.()}
        aria-label="Close"
      />
      <div
        className="raf-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="raf-update-commission-title"
        style={{ '--raf-primary': THEME }}
      >
        <div className="raf-modal__head">
          <div className="raf-modal__title">
            <Percent className="w-5 h-5" aria-hidden="true" />
            <h2 id="raf-update-commission-title">Update commission</h2>
          </div>
          <button
            type="button"
            className="raf-modal__close"
            onClick={() => !saving && onClose?.()}
            aria-label="Close"
            disabled={saving}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="raf-modal__body">
            <p>
              Change the commission share for{' '}
              <strong>{referral.name || referral.email || 'this sub partner'}</strong>.
            </p>
            <label className="raf-modal__label" htmlFor="raf-update-commission">
              Sub commission
            </label>
            <select
              id="raf-update-commission"
              className="raf-modal__input raf-modal__select"
              value={percentage}
              onChange={(e) => {
                setPercentage(e.target.value);
                if (error) setError('');
              }}
              disabled={saving || loadingOptions || options.length === 0}
            >
              {loadingOptions ? (
                <option value="">Loading…</option>
              ) : options.length === 0 ? (
                <option value="">No options available</option>
              ) : (
                options.map((opt) => (
                  <option key={opt.id} value={String(opt.percentage)}>
                    {opt.label}
                  </option>
                ))
              )}
            </select>
            {error ? <span className="raf-modal__error">{error}</span> : null}
          </div>

          <div className="raf-modal__foot">
            <button
              type="button"
              className="raf-modal__btn raf-modal__btn--ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="raf-modal__btn raf-modal__btn--primary"
              disabled={saving || loadingOptions || options.length === 0}
            >
              {saving ? 'Saving…' : 'Update commission'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateCommissionModal;
